import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";

const intro = [
  "Boardroom (“Boardroom,” “we,” “our,” or “us”) respects your privacy. This Privacy Policy explains how information is collected, used, processed, stored, and protected when you use the Boardroom application, website, and related services (collectively, the “Service”).",
  "Boardroom is a decision-support application designed to help users organize situations, explore options, consider different perspectives, make decisions, create action plans, and review outcomes.",
  "By using Boardroom, you acknowledge the practices described in this Privacy Policy.",
];

const sections = [
  {
    num: "01",
    title: "Information We Collect",
    blocks: [
      { paras: ["The information we collect depends on how you use Boardroom."] },
      {
        heading: "A. Account Information",
        paras: ["If you create an account, we may collect information such as:"],
        bullets: [
          "Email address",
          "Name, if provided",
          "Authentication information",
          "Account identifiers",
          "Account creation and login information",
        ],
      },
      {
        paras: [
          "If you use a third-party authentication provider, such as Google, we may receive information necessary to authenticate your account in accordance with the permissions you grant that provider.",
          "We do not receive your Google password.",
        ],
      },
      {
        heading: "B. Case Content and User-Generated Content",
        paras: [
          "Boardroom allows users to create “Cases” concerning decisions, situations, goals, problems, opportunities, or other topics.",
          "Information you voluntarily provide may include:",
        ],
        bullets: [
          "Case descriptions",
          "Answers to interview questions",
          "Goals",
          "Decisions",
          "Options being considered",
          "Notes",
          "Action plans",
          "Outcomes",
          "Feedback",
          "Other information you choose to enter",
        ],
      },
      {
        paras: [
          "The nature of Boardroom means users may voluntarily enter personal or sensitive information.",
          "You control what information you provide and should avoid entering information that is unnecessary for the purpose of your Case.",
        ],
      },
      {
        heading: "C. Guest Usage",
        paras: [
          "Boardroom may allow you to use certain features without creating an account.",
          "Information associated with guest Cases may be stored temporarily so the Service can maintain your session and provide the requested functionality.",
          "Guest Cases are not intended for permanent storage.",
          "If you create an account while a temporary Case remains available, Boardroom may associate that Case with your new account.",
          "Temporary guest information may be automatically deleted after the applicable retention period.",
        ],
      },
      {
        heading: "D. Voice Features",
        paras: [
          "If you choose to use voice features, audio may be transmitted to a speech-processing provider for transcription or generation of spoken responses.",
          "Depending on the implementation of the Service, Boardroom may store the resulting transcript as part of your Case.",
          "Boardroom does not intentionally retain raw voice recordings longer than necessary to provide the requested voice functionality unless the Service specifically informs you otherwise.",
        ],
      },
      {
        heading: "E. Technical and Usage Information",
        paras: [
          "We may automatically collect limited technical information necessary to operate, secure, improve, and troubleshoot the Service, including:",
        ],
        bullets: [
          "Device type",
          "Operating system",
          "Application version",
          "General usage events",
          "Error and crash information",
          "Authentication events",
          "Approximate network information",
          "Performance information",
        ],
      },
      {
        paras: [
          "We seek to avoid sending the contents of private Cases to general analytics services when such content is not necessary for analytics.",
        ],
      },
    ],
  },
  {
    num: "02",
    title: "How We Use Information",
    blocks: [
      {
        paras: ["We may use information to:"],
        bullets: [
          "Provide and operate Boardroom",
          "Create and manage user accounts",
          "Save and retrieve Cases",
          "Generate AI-assisted questions, analyses, perspectives, recommendations, and action plans",
          "Maintain Case history",
          "Generate Decision Profile insights",
          "Provide voice functionality",
          "Generate Case reports",
          "Provide follow-up and reminder functionality",
          "Synchronize information across devices",
          "Improve product reliability and performance",
          "Detect fraud, abuse, security threats, or technical problems",
          "Respond to support requests",
          "Comply with applicable law",
          "Protect the rights and safety of users, Boardroom, and others",
        ],
      },
      { paras: ["We do not sell your personal information to advertisers."] },
    ],
  },
  {
    num: "03",
    title: "Artificial Intelligence Processing",
    blocks: [
      {
        paras: [
          "Boardroom uses artificial intelligence services to provide its core functionality.",
          "When you use AI-powered features, relevant portions of the information you provide may be transmitted to third-party AI service providers so they can process the request and return a response.",
          "This may include:",
        ],
        bullets: [
          "Case descriptions",
          "Interview responses",
          "Relevant Case findings",
          "Previous information relevant to the current Case",
          "User-confirmed Decision Profile information",
        ],
      },
      {
        paras: [
          "We attempt to limit AI processing to information reasonably necessary to provide the requested feature.",
          "AI-generated responses may be inaccurate, incomplete, or inappropriate for a particular situation. Users are responsible for evaluating AI-generated information before relying on it.",
        ],
      },
    ],
  },
  {
    num: "04",
    title: "Decision Profile and Personalization",
    blocks: [
      {
        paras: [
          "If you maintain an account, Boardroom may analyze information from your Cases to identify patterns that may help personalize future decision support.",
          "For example, Boardroom may identify recurring goals, constraints, decision patterns, or previously recorded outcomes.",
          "These insights are generated from information you provide and should not be treated as psychological, medical, or professional diagnoses.",
          "Where available, users may correct, reject, or exclude certain information from personalization.",
        ],
      },
    ],
  },
  {
    num: "05",
    title: "How Information Is Shared",
    blocks: [
      {
        paras: [
          "We do not make private Cases publicly available by default.",
          "We may share or process information with service providers that help us operate Boardroom, including providers of:",
        ],
        bullets: [
          "Cloud hosting",
          "Database infrastructure",
          "Authentication",
          "Artificial intelligence",
          "Speech processing",
          "Application monitoring",
          "Email delivery",
          "Security",
          "Payment processing if paid services are introduced",
        ],
      },
      {
        paras: [
          "These providers may process information only as necessary to provide their respective services, subject to their applicable agreements and privacy practices.",
          "We may also disclose information if reasonably necessary to:",
        ],
        bullets: [
          "Comply with applicable law or valid legal process",
          "Protect the security or integrity of Boardroom",
          "Investigate fraud or abuse",
          "Protect the rights, property, or safety of users or others",
          "Complete a merger, acquisition, financing, restructuring, or sale of assets, subject to applicable privacy requirements",
        ],
      },
    ],
  },
  {
    num: "06",
    title: "Public Sharing",
    blocks: [
      {
        paras: [
          "Cases are private by default.",
          "If Boardroom introduces optional community or sharing features, a Case will not become public merely because it exists in your account.",
          "You must affirmatively choose to share information.",
          "Where available, Boardroom may generate a separate shareable version of a Case and provide tools to review or remove information before publication.",
          "You are responsible for reviewing information before making it public.",
          "Do not publish another person's private or identifying information without appropriate authorization.",
        ],
      },
    ],
  },
  {
    num: "07",
    title: "Data Retention",
    blocks: [
      {
        paras: [
          "We retain account information and saved Cases for as long as reasonably necessary to provide the Service or until the information is deleted, subject to legitimate legal, security, backup, and operational requirements.",
          "Temporary guest Cases may be retained for a limited period and then automatically deleted.",
          "Some information may remain temporarily in encrypted backups or security logs after deletion until those systems rotate or expire.",
        ],
      },
    ],
  },
  {
    num: "08",
    title: "Data Security",
    blocks: [
      {
        paras: [
          "We use reasonable administrative, technical, and organizational safeguards designed to protect information against unauthorized access, alteration, disclosure, or destruction.",
          "However, no internet service, storage system, or transmission method can guarantee absolute security.",
          "Users should not store passwords, payment card numbers, government identification numbers, authentication credentials, or other unnecessary highly sensitive information inside Cases.",
        ],
      },
    ],
  },
  {
    num: "09",
    title: "User Control and Deletion",
    blocks: [
      {
        paras: ["Depending on available features, users may be able to:"],
        bullets: [
          "Review saved Cases",
          "Edit Case information",
          "Delete individual Cases",
          "Exclude Cases from personalization",
          "Export certain account information",
          "Delete their account",
        ],
      },
      {
        paras: [
          "When an account is deleted, we will take reasonable steps to delete or de-identify associated personal information, subject to information we are permitted or required to retain for legal, security, fraud-prevention, backup, or operational purposes.",
        ],
      },
    ],
  },
  {
    num: "10",
    title: "Third-Party Services",
    blocks: [
      {
        paras: [
          "Boardroom relies on third-party technology providers.",
          "Those providers may process information according to their own privacy policies and contractual obligations.",
          "Boardroom is not responsible for third-party websites or services that you independently access outside the Service.",
        ],
      },
    ],
  },
  {
    num: "11",
    title: "Payments and Future Paid Services",
    blocks: [
      {
        paras: [
          "Boardroom may introduce optional paid features, subscriptions, or a Pro plan in the future.",
          "If payment features are introduced, payments may be processed by third-party payment providers or applicable app-store payment systems.",
          "Boardroom does not intend to directly store complete payment card information when payments are processed by those providers.",
          "Any material changes to data practices associated with paid services will be reflected in this Privacy Policy.",
        ],
      },
    ],
  },
  {
    num: "12",
    title: "Professional and High-Risk Information",
    blocks: [
      {
        paras: [
          "Boardroom is a decision-support tool.",
          "It is not a substitute for licensed medical, mental-health, legal, financial, emergency, or other professional services.",
          "Users should exercise caution when entering highly sensitive information.",
          "AI-generated information should not be treated as professional advice, diagnosis, representation, or a guarantee of any particular outcome.",
        ],
      },
    ],
  },
  {
    num: "13",
    title: "Children's Privacy",
    blocks: [
      {
        paras: [
          "Boardroom is not intended for children under 13.",
          "We do not knowingly collect personal information from children under 13.",
          "If we learn that personal information from a child under 13 has been collected in violation of applicable law, we will take reasonable steps to delete it.",
          "Additional age restrictions may apply depending on jurisdiction or features offered through the Service.",
        ],
      },
    ],
  },
  {
    num: "14",
    title: "International Users",
    blocks: [
      {
        paras: [
          "Information may be processed or stored in countries other than the country in which you live.",
          "Where required, we will use appropriate safeguards for international transfers of personal information.",
        ],
      },
    ],
  },
  {
    num: "15",
    title: "Changes to This Privacy Policy",
    blocks: [
      {
        paras: [
          "We may update this Privacy Policy as Boardroom develops.",
          "When material changes are made, we will update the “Last Updated” date and may provide additional notice when appropriate.",
          "Continued use of the Service following an update constitutes acknowledgment of the updated Privacy Policy to the extent permitted by law.",
        ],
      },
    ],
  },
  {
    num: "16",
    title: "Contact Us",
    blocks: [
      {
        paras: [
          "If you have questions about this Privacy Policy, privacy practices, or a request concerning your information, contact:",
        ],
      },
      {
        paras: [
          "Boardroom",
          "Operated by Aldo Labs",
          "Email: support@boardroom.solutions",
          "Website: boardroom.solutions",
        ],
      },
    ],
  },
];

function Block({ block }) {
  return (
    <div>
      {block.heading && (
        <h3 className="font-mono-label text-[10px] tracking-[0.25em] uppercase text-[#C9B896]/80 mb-4 mt-2">
          {block.heading}
        </h3>
      )}
      {block.paras?.map((p, i) => (
        <p key={i} className="text-sm md:text-base leading-relaxed text-stone-400 mb-4">
          {p}
        </p>
      ))}
      {block.bullets && (
        <ul className="mb-4 space-y-2">
          {block.bullets.map((b) => (
            <li key={b} className="flex gap-3 text-sm md:text-base leading-relaxed text-stone-400">
              <span className="mt-[9px] w-1 h-1 rounded-full bg-[#C9B896]/70 shrink-0" />
              {b}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Privacy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="privacy-page" className="min-h-screen bg-[#0E0D0B]">
      <header className="flex items-center justify-between px-6 md:px-12 py-6 border-b border-white/[0.07]">
        <Link
          to="/"
          data-testid="privacy-logo"
          className="font-serif-display text-xl tracking-tight text-white"
        >
          Boardroom<sup className="font-mono-label text-[9px] ml-1">®</sup>
        </Link>
        <Link
          to="/"
          data-testid="privacy-back-link"
          className="flex items-center gap-2 font-mono-label text-[10px] tracking-[0.25em] uppercase text-stone-400 hover:text-white transition-colors duration-300"
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
          Back
        </Link>
      </header>

      <main className="px-6 md:px-12 py-20 md:py-28 max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-mono-label text-[10px] tracking-[0.3em] uppercase text-[#C9B896]/80 mb-8">
            Legal — Last Updated August 5, 2026
          </p>
          <h1 className="font-serif-display font-light tracking-tighter leading-[0.95] text-5xl md:text-7xl text-white mb-12">
            Privacy <span className="italic">Policy.</span>
          </h1>
          <div className="mb-20">
            {intro.map((p, i) => (
              <p key={i} className="text-sm md:text-base leading-relaxed text-stone-400 mb-4">
                {p}
              </p>
            ))}
          </div>
        </motion.div>

        <div className="space-y-16">
          {sections.map((s) => (
            <motion.section
              key={s.num}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`privacy-section-${s.num}`}
              className="border-t border-white/[0.07] pt-10"
            >
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                <div className="md:col-span-4">
                  <p className="font-mono-label text-xs tracking-[0.3em] text-[#C9B896]/80 mb-3">
                    {s.num} —
                  </p>
                  <h2 className="font-serif-display font-light tracking-tight text-3xl text-white">
                    {s.title}
                  </h2>
                </div>
                <div className="md:col-span-8 space-y-6">
                  {s.blocks.map((b, i) => (
                    <Block key={i} block={b} />
                  ))}
                </div>
              </div>
            </motion.section>
          ))}
        </div>
      </main>

      <footer className="px-6 md:px-12 py-8 border-t border-white/[0.07]">
        <p className="font-mono-label text-[9px] tracking-[0.25em] uppercase text-white/40">
          © 2026 Boardroom. All decisions final.
        </p>
      </footer>
    </div>
  );
}
